import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';

export interface TeacherHit {
  email: string | null;
  first_name: string | null;
  id: number;
  last_name: string | null;
  role: string;
  subscriber: { name?: string | null } | null;
}

interface SearchRow extends TeacherHit { user_type: 'staffer' | 'student' }

// One box, one of the user search's fields: an email if it has an @, a staffer id if it is all digits,
// a full name both ways round (each half narrowed by the other, so whichever name is rarer finds them),
// otherwise first and last name together.
interface Search { field: string; q: string; narrow?: { field: 'first_name' | 'last_name'; prefix: string } }

const searchesFor = (q: string): Search[] => {
  if (q.includes('@')) return [{ field: 'email', q }];
  if (/^\d+$/.test(q)) return [{ field: 'id', q }];

  const words = q.split(/\s+/);
  if (words.length > 1) {
    const first = words[0];
    const last = words[words.length - 1];

    return [{ field: 'first_name', narrow: { field: 'last_name', prefix: last }, q: first },
      { field: 'last_name', narrow: { field: 'first_name', prefix: first }, q: last }];
  }

  return [{ field: 'last_name', q }, { field: 'first_name', q }];
};

const search = async ({ field, narrow, q }: Search) => {
  const { data } = await axios.get(Routes.plato_api_sysadmin_user_search_path({
    exact: false, field, per_type: narrow == null ? 10 : 50, q, role: 'all',
  }));

  return (data.data as SearchRow[]).filter(row => row.user_type === 'staffer'
    && (narrow == null || (row[narrow.field] ?? '').toLowerCase().startsWith(narrow.prefix.toLowerCase())));
};

const useTeacherSearch = (query: string) => {
  const q = query.trim();

  return useQuery({
    enabled: q.length >= 2,
    keepPreviousData: true,
    queryFn: async (): Promise<TeacherHit[]> => {
      const results = await Promise.all(searchesFor(q).map(search));
      const seen = new Set<number>();

      return results.flat().filter(row => !seen.has(row.id) && seen.add(row.id));
    },
    queryKey: ['gradeSyncTeacherSearch', q],
  });
};

export default useTeacherSearch;
